/**
 * AI PROCESSING STEPS — the executable AI steps of one candidacy's pipeline, and the rules an
 * owner's comparison rerun on /admin/judge-calls follows (migration 0093).
 *
 * ONLY REAL STEPS. Each step here is an existing call site that makes a provider call:
 *
 *   Resume     resume.extract_text   the resume file → plain text (question grounding)
 *              resume.score          the resume file → rubric component scores
 *   Interview  transcript.transcribe the recording  → full transcript        (Azure Speech)
 *              transcript.align      the transcript → per-question answers   (model)
 *              judge.score           the answers    → an evidence-based evaluation (model, 0099)
 *
 * The resume's overall score (computeOverall) and the interview's capability + coverage
 * (capabilityOf / coverageOf) are ARITHMETIC over those outputs. They are shown, never offered
 * as a rerun or a model choice — there is no model to choose.
 *
 * DEPENDENCIES, as the code has them — not as a tidy diagram would:
 *   - Extraction and evaluation each read the resume FILE. Neither needs the other.
 *   - Alignment needs a full transcript. No live table keeps one (only the per-answer slices do),
 *     so it comes from transcription in the same run, or an earlier run's transcription.
 *   - Evaluation needs a completed interview and a first score (it carries that score's identity
 *     and integrity result over, exactly like today's rescore). It grades the live answers unless
 *     alignment runs in the same run, in which case it grades that alignment's text.
 *
 * PROMOTION. A rerun never changes the live result. "Make authoritative" is explicit, per step,
 * and refused once a human has decided. Transcription and alignment have no live result of their
 * own: alignment goes live only WITH the evaluation that used it (the verified-transcript rule —
 * recording-derived words must never sit beside a score computed from different text).
 *
 * PURE: the API validates with it, the admin page renders with it, the tests pin it.
 */
export const AI_STEPS = [
    {
        key: "resume.extract_text",
        label: "Resume extraction",
        group: "resume",
        seq: 1,
        runner: "api",
        kind: "model",
        activity: "resume.extract_text",
        service: null,
        promotable: "self",
        description: "Transcribes the resume file to plain text — the grounding for tailored questions.",
    },
    {
        key: "resume.score",
        label: "Resume evaluation",
        group: "resume",
        seq: 2,
        runner: "api",
        kind: "model",
        activity: "resume.score",
        service: null,
        promotable: "self",
        description: "Scores the resume file against the requisition's fixed rubric. The overall is computed from the component scores.",
    },
    {
        key: "transcript.transcribe",
        label: "Transcription",
        group: "interview",
        seq: 3,
        runner: "worker",
        kind: "speech",
        activity: null,
        service: "azure_speech:fast_transcription",
        promotable: "never",
        description: "Azure Speech fast transcription of the interview recording, in the session's language.",
    },
    {
        key: "transcript.align",
        label: "Transcript alignment",
        group: "interview",
        seq: 4,
        runner: "worker",
        kind: "model",
        activity: "transcript.align",
        service: null,
        promotable: "with_evaluation",
        description: "Splits the recording's transcript into the candidate's verbatim answer to each question.",
    },
    {
        key: "judge.score",
        label: "Interview evaluation",
        group: "interview",
        seq: 5,
        runner: "worker",
        kind: "model",
        activity: "judge.score",
        service: null,
        promotable: "self",
        description: "Scores every competency and answer with cited evidence. The composite and recommendation are computed from it.",
    },
];
export const AI_STEP_KEYS = AI_STEPS.map((s) => s.key);
export function aiStep(key) {
    return AI_STEPS.find((s) => s.key === key) ?? null;
}
export function isAiStepKey(key) {
    return typeof key === "string" && AI_STEPS.some((s) => s.key === key);
}
/**
 * Per step: can it run, given the facts and the steps selected alongside it? Selection matters for
 * exactly one edge — alignment can use a transcription selected in the same run.
 */
export function aiStepAvailability(facts, selected = []) {
    const sel = new Set(selected);
    const ok = (note) => ({ available: true, note });
    const no = (note) => ({ available: false, note });
    const resume = facts.hasResumeFile ? ok("Reads the stored resume file") : no("No resume file stored");
    const transcribe = !facts.hasInterview
        ? no("No completed interview")
        : facts.hasRecording
            ? ok("Reads the interview recording")
            : no("No recording");
    const align = !facts.hasInterview
        ? no("No completed interview")
        : sel.has("transcript.transcribe") && transcribe.available
            ? ok("Uses this run's transcription")
            : facts.hasPriorTranscript
                ? ok("Uses the latest transcription run")
                : no(facts.hasRecording ? "Waiting for transcript — select Transcription too" : "No recording to transcribe");
    const judge = !facts.hasInterview
        ? no("No completed interview")
        : sel.has("transcript.align") && align.available
            ? ok("Grades this run's aligned answers")
            : // No live evaluation yet is NOT a blocker (0099): a run is a non-live evaluation and "Make authoritative"
                // makes the first one current. Before this the owner had no way to run a candidate's first evaluation.
                !facts.hasAuthoritativeScore
                    ? ok("No live evaluation yet — this run is not live until you make it authoritative")
                    : ok("Grades the answers the live score used");
    return {
        "resume.extract_text": resume,
        "resume.score": resume,
        "transcript.transcribe": transcribe,
        "transcript.align": align,
        "judge.score": judge,
    };
}
/** The selected steps that cannot run, each with its reason. Empty = the selection is valid. */
export function invalidAiSteps(facts, selected) {
    const avail = aiStepAvailability(facts, selected);
    return selected.filter((s) => !avail[s].available).map((s) => ({ step: s, reason: avail[s].note }));
}
/**
 * Drop selected steps that the rest of the selection no longer supports (unticking Transcription
 * can strand Alignment). Repeats until stable — each removal can strand the next step.
 */
export function pruneAiStepSelection(facts, selected) {
    let cur = [...new Set(selected)];
    for (;;) {
        const avail = aiStepAvailability(facts, cur);
        const next = cur.filter((s) => avail[s].available);
        if (next.length === cur.length)
            return AI_STEP_KEYS.filter((k) => next.includes(k));
        cur = next;
    }
}
export function formatTokens(n) {
    if (n == null)
        return "—";
    if (n < 1000)
        return String(n);
    if (n < 1_000_000)
        return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}K`;
    return `${(n / 1_000_000).toFixed(1)}M`;
}
export function formatUsd(n) {
    if (n == null)
        return "—";
    if (n === 0)
        return "$0";
    if (n < 0.0001)
        return "<$0.0001";
    return `$${n < 1 ? n.toFixed(4) : n.toFixed(2)}`;
}
export function formatDuration(seconds) {
    if (seconds == null)
        return "—";
    const s = Math.round(seconds);
    const m = Math.floor(s / 60);
    return m > 0 ? `${m}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`;
}
const STATUS_WORD = { queued: "queued", running: "running", succeeded: "✓", failed: "✗ failed" };
/** One cell as a single line of text — the unit of the copied table. */
export function aiStepCellText(step, cell) {
    if (!cell)
        return "— not run";
    const def = aiStep(step);
    const who = cell.modelLabel ?? cell.model ?? (cell.status === "queued" || cell.status === "running" ? "current routing" : "no model call");
    const parts = [`${who} ${STATUS_WORD[cell.status]}`];
    if (def?.kind === "speech") {
        if (cell.audioSeconds != null)
            parts.push(formatDuration(cell.audioSeconds));
    }
    else if (cell.inputTokens != null || cell.outputTokens != null) {
        parts.push(`in ${formatTokens(cell.inputTokens)} / out ${formatTokens(cell.outputTokens)}`);
    }
    parts.push(formatUsd(cell.costUsd));
    if (cell.calls > 1)
        parts.push(`${cell.calls} calls${cell.failedCalls ? `, ${cell.failedCalls} failed` : ""}`);
    if (cell.fallbackCalls > 0)
        parts.push(`${cell.fallbackCalls} via fallback`);
    const s = cell.summary ?? {};
    if (typeof s.overall === "number")
        parts.push(`overall ${s.overall}`);
    if (typeof s.composite === "number")
        parts.push(`composite ${s.composite}`);
    if (typeof s.recommendation === "string")
        parts.push(`rec. ${s.recommendation}`);
    if (typeof s.coverage === "string")
        parts.push(`coverage ${s.coverage}`);
    if (cell.isAuthoritative)
        parts.push("**LIVE**");
    if (cell.promotion === "pending")
        parts.push("promotion pending");
    if (cell.error)
        parts.push(`error: ${cell.error.slice(0, 120)}`);
    return parts.join(" · ").replace(/\|/g, "\\|").replace(/\s*\n\s*/g, " ");
}
/**
 * The whole comparison history as Markdown — one row per run, one column per step — with enough
 * context above it that it can be pasted into an AI chat and asked about models, cost, tokens,
 * differences between runs, what was rerun, and what is live.
 */
export function aiRunsMarkdown(input) {
    const head = ["Run", ...AI_STEPS.map((s) => s.label), "Total cost"];
    const lines = [
        `TalentHop AI processing history — candidacy ${input.candidacyId}${input.orgName ? ` (${input.orgName})` : ""}`,
        `Candidacy state: ${input.candidacyState}. Copied ${(input.copiedAt ?? new Date()).toISOString()}.`,
        "Costs are estimates from provider list prices (ai_call_log); USD at the platform's fixed FX rate.",
        "Each run row shows only the steps it executed. **LIVE** marks the result the recruiter's report uses now; a run's results stay non-live until an owner makes them authoritative.",
    ];
    const computed = Object.entries(input.computed ?? {}).filter(([, v]) => v != null);
    if (computed.length > 0) {
        const text = computed.map(([k, v]) => `${k}: ${String(v).replace(/\.\s*$/, "")}`).join("; ");
        lines.push(`Live computed results — ${text}.`);
    }
    lines.push("", `| ${head.join(" | ")} |`, `|${head.map(() => "---").join("|")}|`, ...input.runs.map((r) => {
        const label = r.runNo == null
            ? r.label
            : `${r.label}${r.parentRunNo != null ? ` (after #${r.parentRunNo})` : ""}${r.reason ? ` — ${r.reason.replace(/\|/g, "\\|").replace(/\s*\n\s*/g, " ").slice(0, 80)}` : ""}`;
        const cells = AI_STEPS.map((s) => aiStepCellText(s.key, r.steps[s.key]));
        return `| ${[label, ...cells, `${formatUsd(r.totalCostUsd)} (₹${r.totalCostInr.toFixed(2)})`].join(" | ")} |`;
    }));
    return lines.join("\n");
}
//# sourceMappingURL=aiSteps.js.map